import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Ticket, Check, Download, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TicketSuccessPage() {
  const [searchParams] = useSearchParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ticketId = searchParams.get("ticket_id");
    const sessionId = searchParams.get("session_id");
    if (ticketId) {
      axios.get(`${API}/tickets/confirm/${ticketId}?session_id=${sessionId || ""}`)
        .then(r => setTicket(r.data))
        .catch(() => toast.error("Could not load ticket"))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [searchParams]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center pt-20">
      <p className="font-body text-zinc-500">Loading your ticket...</p>
    </div>
  );

  const isPending = ticket && ticket.status === "pending_approval";
  const isValid = ticket && (ticket.status === "valid" || ticket.status === "pending_approval");

  if (!ticket || !isValid) return (
    <div className="min-h-screen flex items-center justify-center pt-20">
      <div className="text-center px-6">
        <p className="font-subheading text-xl uppercase tracking-wider text-zinc-400 mb-4">Ticket not found or payment pending</p>
        <a href="/events" className="font-subheading text-xs uppercase tracking-widest border border-white/20 text-white px-6 py-2.5 hover:border-white transition-colors inline-flex items-center gap-2">
          <ArrowLeft size={14} /> Back to Events
        </a>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen pt-32 pb-32" data-testid="ticket-success-page">
      <div className="max-w-lg mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
          {isPending ? (
            <>
              <div className="w-20 h-20 border-2 border-yellow-500/30 bg-yellow-500/10 flex items-center justify-center mx-auto mb-6">
                <Check size={36} className="text-yellow-400" />
              </div>
              <h1 className="font-subheading text-3xl uppercase tracking-tight mb-2">Ticket Reserved!</h1>
              <p className="font-body text-zinc-500">Your ticket will be activated once payment is verified</p>
              <p className="font-body text-xs text-yellow-400/80 mt-2">You can download your ticket now. It will become scannable after admin approval.</p>
            </>
          ) : (
            <>
              <div className="w-20 h-20 border-2 border-green-500/30 bg-green-500/10 flex items-center justify-center mx-auto mb-6">
                <Check size={36} className="text-green-400" />
              </div>
              <h1 className="font-subheading text-3xl uppercase tracking-tight mb-2">Ticket Confirmed!</h1>
              <p className="font-body text-zinc-500">Your ticket has been issued</p>
            </>
          )}
        </motion.div>

        <div className="border border-white/10 p-8 mb-8">
          <div className="text-center mb-6">
            {ticket.qr_code && (
              <img src={`data:image/png;base64,${ticket.qr_code}`} alt="Ticket QR Code" className="w-48 h-48 mx-auto mb-4 bg-white p-2" />
            )}
          </div>
          <div className="space-y-3">
            <div className="flex justify-between border-b border-white/5 pb-2">
              <span className="font-body text-xs uppercase tracking-wider text-zinc-500">Attendee</span>
              <span className="font-body text-sm text-white">{ticket.attendee_name}</span>
            </div>
            <div className="flex justify-between border-b border-white/5 pb-2">
              <span className="font-body text-xs uppercase tracking-wider text-zinc-500">Ticket Type</span>
              <span className="font-body text-sm text-white">{ticket.ticket_type}</span>
            </div>
            <div className="flex justify-between border-b border-white/5 pb-2">
              <span className="font-body text-xs uppercase tracking-wider text-zinc-500">Amount</span>
              <span className="font-body text-sm text-white">{ticket.price > 0 ? `$${ticket.price.toFixed(2)}` : "Free"}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-body text-xs uppercase tracking-wider text-zinc-500">Ticket ID</span>
              <span className="font-body text-sm text-zinc-400">{ticket.id.slice(0, 8).toUpperCase()}</span>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <button
            onClick={() => window.open(`${API}/tickets/${ticket.id}/pdf`, "_blank")}
            data-testid="download-ticket-btn"
            className="w-full font-subheading text-sm uppercase tracking-widest bg-white text-black px-8 py-3.5 hover:bg-zinc-200 transition-colors flex items-center justify-center gap-2"
          >
            <Download size={16} /> Download Ticket PDF
          </button>
          <a href="/events" className="w-full font-subheading text-xs uppercase tracking-widest border border-white/20 text-white px-6 py-3 hover:border-white transition-colors text-center flex items-center justify-center gap-2">
            <Ticket size={14} /> Back to Events
          </a>
        </div>

        <p className="font-body text-xs text-zinc-600 text-center mt-6">
          Present this QR code at the door for entry. One-time use only.
        </p>
      </div>
    </div>
  );
}
